import React, { useState, useEffect } from "react";
import SubscriptionPlans from "./SubscriptionPlans";
import { Dialog, Transition } from "@headlessui/react";
import { Fragment } from "react";
import { fetchProducts } from "../hooks/useProducts";

const SubscribeButton: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);

  // Warm the shared products cache as soon as the button renders, so the
  // plans are ready instantly when the user opens the modal (no skeleton).
  useEffect(() => {
    fetchProducts().catch(() => {
      // Ignored — the modal surfaces the error via useProducts when opened.
    });
  }, []);

  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        className="w-full px-5 py-3 bg-gradient-to-r from-[#47b3fe] to-indigo-600 hover:from-blue-500 hover:to-indigo-700 text-white text-sm font-semibold rounded-xl
                 shadow-sm hover:shadow-md transition-all duration-200
                 flex items-center justify-center gap-2.5"
      >
        <i className="fas fa-crown text-yellow-300 text-xs"></i>
        <span>Upgrade to Premium</span>
      </button>

      <Transition appear show={isOpen} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-[60]"
          onClose={() => setIsOpen(false)}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-200"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-150"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-4xl transform overflow-hidden rounded-2xl bg-gradient-to-b from-slate-900 to-slate-950 text-left align-middle shadow-2xl transition-all border border-white/5">
                  {/* Modal Header */}
                  <div className="relative px-8 py-5 flex items-center border-b border-white/5">
                    <div className="w-10 h-10 rounded-xl overflow-hidden border border-white/10 mr-3 flex-shrink-0">
                      <img
                        src="/albert.jpg"
                        alt="Albert Einstein"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div>
                      <Dialog.Title
                        as="h2"
                        className="text-base font-bold text-white"
                      >
                        Upgrade Your Learning Experience
                      </Dialog.Title>
                      <p className="text-slate-400 text-xs mt-0.5">
                        Unlock unlimited AI-powered learning materials and quizzes
                      </p>
                    </div>
                    <button
                      onClick={() => setIsOpen(false)}
                      className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-lg text-white/40 hover:text-white hover:bg-white/10 transition-colors"
                    >
                      <i className="fas fa-times text-sm"></i>
                    </button>
                  </div>

                  <div className="grid grid-cols-5">
                    {/* Benefits panel */}
                    <div className="col-span-2 p-6 border-r border-white/5 flex items-center">
                      <div className="space-y-4 w-full">
                        {[
                          {
                            icon: "fas fa-bolt",
                            title: "Fast Learning",
                            description:
                              "Learn any topic in minutes with personalized summaries",
                          },
                          {
                            icon: "fas fa-brain",
                            title: "Better Retention",
                            description:
                              "Quizzes and flashcards designed for maximum recall",
                          },
                          {
                            icon: "fas fa-infinity",
                            title: "Unlimited Topics",
                            description:
                              "No restrictions on what or how much you can learn",
                          },
                        ].map((benefit, index) => (
                          <div
                            key={index}
                            className="flex gap-3 p-4 bg-white/5 rounded-xl border border-white/5"
                          >
                            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#47b3fe] to-indigo-600 flex items-center justify-center flex-shrink-0">
                              <i className={`${benefit.icon} text-white text-sm`}></i>
                            </div>
                            <div>
                              <p className="text-sm font-semibold text-white">
                                {benefit.title}
                              </p>
                              <p className="text-xs text-slate-400 mt-0.5 leading-relaxed">
                                {benefit.description}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Plans panel */}
                    <div className="col-span-3 p-6 h-[520px] overflow-y-auto custom-scrollbar">
                      <SubscriptionPlans />
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
};

export default SubscribeButton;
